import { ChevronRightIcon, ClockIcon } from '@langchain/untitled-ui-icons';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useRef, useState } from 'react';

import { Button } from '@/design-system';
import { useDefaultDuration } from '@/hooks/useDefaultDuration';
import PendingIcon from '@/icons/PendingIcon.svg?react';
import { cn } from '@/utils/tailwind';

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../Command';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import {
  DateTimeRangePickerValue,
  DurationUnit,
  formatDuration,
  getDateTimeRangeLabel,
  parseRelativeDate,
} from '../RunsTable/utils/getDateTimeRangeLabel';
import { SpecificDateTime } from './SpecificDateTime';

dayjs.extend(utc);

const DURATION_OPTIONS = [
  '1h',
  '3h',
  '6h',
  '12h',
  '1d',
  '2d',
  '7d',
  '14d',
  '30d',
];

function DurationCommandItem(props: {
  value: string;
  onSelect: (relative: { value: number; unit: DurationUnit }) => void;
  defaultDuration?: string;
  setDefaultDuration?: (value: string) => void;
}) {
  const relative = parseRelativeDate(props.value);

  if (relative == null) return null;

  const renderSetDuration = () => {
    if (props.setDefaultDuration) {
      return props.defaultDuration !== props.value ? (
        <button
          type="button"
          className="ml-auto rounded-md border border-secondary bg-background px-2 py-0.5 text-xs opacity-0 transition-opacity hover:bg-secondary group-hover:opacity-100"
          onClick={() => props.setDefaultDuration?.(props.value)}
        >
          Set default
        </button>
      ) : (
        <span className="ml-auto px-2 py-0.5 text-xs">Default</span>
      );
    }
  };
  return (
    <CommandItem
      className="group"
      value={props.value}
      onSelect={() => props.onSelect(relative)}
    >
      <span className="font-medium">{formatDuration(relative).label}</span>
      <span className="ml-3 text-tertiary">
        {formatDuration(relative).value}
      </span>
      {renderSetDuration()}
    </CommandItem>
  );
}

export function DateTimeRangePicker(props: {
  value: DateTimeRangePickerValue;
  onChange: (value: DateTimeRangePickerValue) => void;
  hideAllTime?: boolean;
  context?: 'runsTable' | 'conversationsList' | 'dashboard';
  align?: 'start' | 'end';
  triggerClassName?: string;
}) {
  const [popover, setPopover] = useState(false);
  const [specificOpen, setSpecificOpen] = useState(() =>
    Boolean(props.value?.start_time || props.value?.end_time)
  );
  const [specificContent, setSpecificContent] = useState(() =>
    Boolean(props.value?.start_time || props.value?.end_time)
  );

  const ref = useRef<HTMLDivElement>(null);

  const { defaultDuration, setDefaultDuration } = useDefaultDuration(
    props.context
  );

  const [inputValue, setInputValue] = useState('');

  function handleSpecificChange(value?: boolean) {
    const newValue = value ?? !specificOpen;
    if (newValue) {
      setSpecificOpen(true);
      setSpecificContent(true);
    } else {
      setSpecificOpen(false);
      setSpecificContent(true);
    }
  }

  const buttonLabel = getDateTimeRangeLabel(props.value);

  return (
    <Popover open={popover} onOpenChange={setPopover}>
      <div className="flex flex-shrink-0 items-stretch">
        <PopoverTrigger asChild>
          <Button
            variant="outlined"
            color="secondary"
            size="sm"
            leftDecorator={PendingIcon}
            data-testid="date-time-range-picker"
          >
            {buttonLabel}
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        ref={ref}
        align={props.align ?? 'start'}
        className={cn(
          'flex overflow-hidden p-0 transition-all',
          specificOpen ? 'w-[530px]' : 'w-[301px]'
        )}
        onTransitionEnd={(e) => {
          if (e.target === ref.current) {
            if (!specificOpen && specificContent) {
              setSpecificContent(false);
            }
          }
        }}
        avoidCollisions={false}
        side={'bottom'}
      >
        <div className="flex min-h-[400px] min-w-[300px] shrink-0 flex-col items-stretch border-r border-tertiary">
          <Command className="flex flex-col">
            <CommandInput
              placeholder="Relative time (Ex: 15m, 1h, 1d, 1w)"
              value={inputValue}
              onValueChange={(value) => setInputValue(value)}
              className="mx-3 mt-3"
              data-testid="date-time-range-picker-input"
            />
            <CommandList>
              <CommandGroup className="m-0 max-h-[420px] overflow-y-auto overflow-x-hidden p-0 [&_[cmdk-group-items]]:m-3">
                {DURATION_OPTIONS.map((value) => {
                  return (
                    <DurationCommandItem
                      key={value}
                      value={value}
                      onSelect={(relative) => {
                        props.onChange({
                          duration: `${relative.value}${relative.unit}`,
                        });
                        handleSpecificChange(false);
                        setPopover(false);
                      }}
                      defaultDuration={
                        props.context ? defaultDuration : undefined
                      }
                      setDefaultDuration={
                        props.context ? setDefaultDuration : undefined
                      }
                    />
                  );
                })}
                {!props.hideAllTime && (
                  <CommandItem
                    onSelect={() => {
                      props.onChange(undefined);
                      handleSpecificChange(false);
                      setPopover(false);
                    }}
                  >
                    All time
                  </CommandItem>
                )}
                {!DURATION_OPTIONS.includes(inputValue.toLocaleLowerCase()) ? (
                  <DurationCommandItem
                    value={inputValue}
                    onSelect={(relative) => {
                      props.onChange({
                        start_time: dayjs
                          .utc()
                          .subtract(relative.value, relative.unit)
                          .toISOString(),
                      });
                      handleSpecificChange(false);
                      setPopover(false);
                    }}
                  />
                ) : null}
              </CommandGroup>
            </CommandList>
          </Command>

          <div className="flex flex-col justify-stretch border-t border-tertiary p-3">
            <button
              type="button"
              className={cn(
                'flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-secondary-hover active:bg-secondary',
                specificOpen && 'bg-secondary'
              )}
              onClick={() => handleSpecificChange()}
            >
              <ClockIcon className="size-5 flex-shrink-0" />
              <span className="flex-grow text-left font-medium">
                Start and end times
              </span>
              <ChevronRightIcon className="size-5 flex-shrink-0" />
            </button>
          </div>
        </div>
        {specificContent && (
          <SpecificDateTime
            defaultValue={props.value}
            onClose={() => handleSpecificChange(false)}
            onSubmit={(value) => {
              props.onChange(value);
              handleSpecificChange(false);
              setPopover(false);
            }}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
